<template>
  <div class="menu">
    <div
      v-for="element in elements"
      :key="element.name"
      class="menu__element"
      @click="chooseAction(element.event)"
    >
      <app-icon
        :name="element.nameIcon"
        width="16px"
        color="#979797"
        cursor="pointer"
      />
      <span class="menu__element-text">
        {{ element.name }}
      </span>
    </div>
    <app-resizable-dropdown
      v-if="props.JobBindings?.length > 1 && jobAccess!==0"
      position="right"
      :resizable="false"
      trigger="hover"
      :hover-delay="300"
      :hover-close-delay="300"
      :close-by-click-outside="false"
      :max-width="320"
      :max-height="236"
      :mobile-title="$t('jobs_label')"
      ref="$jobWindow"
      @open="handleJobsOpen"
      @close="handleJobsClose"
    >
      <template #activator>
        <div class="menu__element">
          <app-icon
            name="go-job"
            width="16px"
            color="#979797"
            cursor="pointer"
          />
          <span class="menu__element-text"> {{ $t("file_go_to_job") }} </span>
          <app-icon
            name="chevron-left"
            class="menu__element-chevron"
            width="16px"
            color="#979797"
            cursor="pointer"
          />
        </div>
      </template>
      <template #default>
        <div class="jobs-list">
          <template v-for="(job, index) of jobs" :key="job.Job.Id">
            <div v-if="index !== 0" class="jobs-list__divider" />
            <file-job-element :job="job.Job" @select-job="onJobSelected" />
          </template>
        </div>
      </template>
    </app-resizable-dropdown>
    <div
      v-else-if="props.JobBindings?.length == 1 && jobAccess!==0"
      class="menu__element"
      :class="{ 'menu__element--disabled': !jobs?.length }"
      @click="onSingleJobClick"
    >
      <app-icon name="go-job" width="16px" color="#979797" cursor="pointer" />
      <span class="menu__element-text"> {{ $t("file_go_to_job") }} </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppIcon from "../ui-kit/icon/AppIcon.vue";
import AppResizableDropdown from "../ui-kit/dropdown/AppResizableDropdown.vue";
import FileJobElement from "./FileJobElement.vue";
import { JobsApi } from "@/ api/JobsApi";
import { Job, JobInfo } from "@/store/jobs";
import { clientPageStore } from "@/store/client-page";
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { JobBinding } from "@/models/interfaces/file/file-model";
import { navigateToJob } from "./jobNavigation";

interface menuElement {
  name: string;
  nameIcon: string;
  event: string;
}

interface Props {
  elements: menuElement[];
  type?: "file" | "folder";
  JobBindings?: JobBinding[];
}

const props = withDefaults(defineProps<Props>(), { type: "folder" });

const clientStore = clientPageStore();
const jobAccess = clientStore.client?.JobAccess;
const isJobsOpen = ref(false);

const handleJobsOpen = () => {
  isJobsOpen.value = true;
};
const handleJobsClose = () => {
  isJobsOpen.value = false;
  emit("jobs-closed");
};

const jobs = ref<JobInfo[]>();

onMounted(async () => {
  jobs.value = props.JobBindings
    ? await Promise.all(
        props.JobBindings.map((el) => JobsApi.getJobInfo(el.JobId)),
      )
    : undefined;
});

const emit = defineEmits(["select", "jobs-closed", "job-selected"]);

const chooseAction = (option: string) => {
  emit("select", option);
};

const route = useRoute();
const router = useRouter();

const onJobSelected = (job: Job) => {
  emit("job-selected");
  navigateToJob(job, route, router);
};

const onSingleJobClick = () => {
  const single = jobs.value?.[0]?.Job;
  if (!single) return;
  onJobSelected(single);
};
</script>

<style lang="scss" scoped>
@import "@/styles/color.scss";
.menu {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 2px 4px;
  background: #ffffff;
  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
  border-radius: 8px;

  &__element {
    position: relative;
    display: flex;
    height: 28px;
    align-items: center;
    padding: 0px 8px;
    gap: 8px;
    cursor: pointer;
    border-radius: 8px;

    &--disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    &:hover {
      background-color: $color-divider;
    }

    &-text {
      font-weight: 400;
      color: $color-text_main;
      font-style: normal;
      font-size: 14px;
      line-height: 18px;
    }

    &-chevron {
      margin-left: auto;
      transform: scaleX(-1);
    }
  }
}

@media (max-width: 480px) {
  .menu {
    width: 100%;
    padding: 8px 16px 24px 16px;
    border-radius: 0;
    box-shadow: none;
    box-sizing: border-box;

    &__element {
      height: 44px;
      padding: 0 8px;
      border-bottom: 1px solid $color-divider;
      border-radius: 0;

      &:last-child {
				border-bottom: none;
			}
    }
  }
}

.jobs-list {
  display: flex;
  flex-direction: column;
  padding: 8px;
  width: 100%;
  overflow-y: auto;
  max-height: 100%;

  &__divider {
    flex-shrink: 0;
    height: 1px;
    background-color: #f2f2f2;
    margin: 4px 0;
  }
}
</style>
